<template>
  <!-- Relatives Supporting Eligibility Case -->
  <div id="supporting">
    <!-- Relation -->
    <div class="form-section">
      <FormField
        labelText="The person who serves/served is my"
        id="relationship"
        :error="v$.formResponse.relationship.$error && v$.formResponse.relationship.$dirty"
        :errorMessage="'This information is required. Please make a selection.'"
      >
        <ul class="radio-group">
          <li v-for="option in referenceData.serviceConnectionRelationshipOptions" :key="option.display">
            <input
              type="radio"
              :id="option.returnValue"
              :value="option.display"
              name="serviceConnectionRelationship"
              v-model="v$.formResponse.relationship.$model"
              :disabled="disableEligibilityFields"
            />
            <label :for="option.returnValue">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>

    <!-- Relative Name Input Field -->
    <div class="form-section" :key="`service-connection-name${disableEligibilityFields ? '-disable' : ''}`">
      <template v-if="disableEligibilityFields">
        <FormField id="service-connection-name" labelText="Name of connected individual">
          <p class="disabled-input">{{ returnWhiteSpaceIfEmptyStr(formResponse.relationName) }}</p>
        </FormField>
      </template>
      <template v-else>
        <FormField
          id="service-connection-name"
          :class="validationStatus(v$.formResponse.relationName)"
          labelText="Name of connected individual"
          placeholder="Please enter full name"
          type="text"
          v-model="v$.formResponse.relationName.$model"
          :error="v$.formResponse.relationName.$error"
          :errorMessage="relationNameError"
        />
      </template>
    </div>

    <!-- Relative Eligibility -->
    <div class="form-section">
      <FormField
        labelText="How are they eligible?"
        id="connectionType"
        :error="v$.formResponse.connectionType.$error && v$.formResponse.connectionType.$dirty"
        :errorMessage="'This information is required. Please make a selection.'"
      >
        <ul class="radio-group">
          <li v-for="option in referenceData.eligibilityOptions" :key="option.display">
            <input
              type="radio"
              :id="option.returnValue"
              :value="option.display"
              name="serviceConnectionEligibility"
              v-model="v$.formResponse.connectionType.$model"
              @change="updateFormFields"
              :disabled="disableEligibilityFields"
            />
            <label :for="option.returnValue">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>
    <!-- Relative Eligibility Information -->
    <div class="form-section">
      <!-- Relative Veteran Information -->
      <div v-if="formResponse.connectionType === Eligibility.SERVING || formResponse.connectionType === Eligibility.EX_SERVING">
        <VeteranInfo
          v-if="formResponse && formResponse.veteranInfo"
          :subFormData="formResponse.veteranInfo"
          :referenceData="referenceData"
          :serviceAgencyLabel="formLabels.veteranInfo.serviceAgencyLabel"
          :serviceTypeLabel="formLabels.veteranInfo.serviceTypeLabel"
          :disableEligibilityFields="disableEligibilityFields"
          :initialData="initialData"
          :serviceConnection="serviceConnection"
          :defaultVeteranInfo="defaultSubFormTemplate.veteranInfo"
          :isChangeCover="isChangeCover"
          @updated="updateVeteranInfo"
        />
      </div>
      <!-- Relative Department/Company Name -->
      <div
        v-else-if="formResponse.connectionType === Eligibility.DPS || formResponse.connectionType === Eligibility.CONTRACTOR"
        :key="`workplace-name${disableEligibilityFields ? '-disable' : ''}`"
      >
        <template v-if="disableEligibilityFields">
          <FormField id="workplace-name" :labelText="formLabels.connectionInfo.connectionNameLabel">
            <p class="disabled-input">{{ returnWhiteSpaceIfEmptyStr(formResponse.connectionName) }}</p>
          </FormField>
        </template>
        <template v-else>
          <FormField
            id="workplace-name"
            :class="validationStatus(v$.formResponse.connectionName)"
            :labelText="formLabels.connectionInfo.connectionNameLabel"
            :placeholder="formLabels.connectionInfo.connectionNamePlaceholder"
            type="text"
            v-model="v$.formResponse.connectionName.$model"
            :error="v$.formResponse.connectionName.$error"
            :errorMessage="workPlaceError"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { required, maxLength, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { FormField } from '@/components';
import { Eligibility } from '@general/js/enums';
import { returnWhiteSpaceIfEmptyStr } from '@/utils';
import { mapMutations } from 'vuex';
import { getServiceAgencyLabel, getServicePeriodLabel, getEligibilityWorkLabel, getEligibilityWorkPlaceHolder, isInvalid } from '@general/js/utils';
import { VeteranInfo } from './components';
import DEFAULT_VETERAN_INFO from './EligibilityUtils';

export default {
  setup() { return { v$: useVuelidate() }; },
  name: 'Supporting',

  data() {
    return {
      formLabels: {
        veteranInfo: {
          serviceAgencyLabel: '',
          serviceTypeLabel: '',
        },
        connectionInfo: {
          connectionNameLabel: '',
          connectionNamePlaceholder: '',
        },
      },
      formResponse: {
        relationName: '',
        relationship: '',
        connectionName: '',
        connectionType: '',
      },
      Eligibility,
      eligibility: Eligibility.RELATED,
      veteranInfoIsInvalid: true,
    };
  },
  components: {
    FormField,
    VeteranInfo,
  },

  props: {
    subFormData: {
      type: Object,
      required: true,
    },
    referenceData: {
      type: Object,
      required: true,
    },
    noEligibilityChange: {
      type: Boolean,
    },
    disableEligibilityFields: {
      type: Boolean,
    },
    initialData: {
      type: Object,
    },
    serviceConnection: {
      type: String,
    },
    defaultSubFormTemplate: {
      type: Object,
    },
    isChangeCover: {
      type: Boolean,
    },
  },

  watch: {
    formResponse: {
      handler() {
        if (this.formResponse) {
          this.$emit('updated', { subFormData: this.formResponse, isInvalid: this.v$.$error || this.v$.$invalid });
        }
      },
      deep: true,
    },
    // Clears and restores prefill data when service connection changes (Self, Partner)
    serviceConnection(newValue) {
      if (this.isChangeCover && this.noEligibilityChange && this.initialData.serviceConnection !== '') {
        if (newValue !== this.initialData.serviceConnection) {
          this.formResponse = JSON.parse(JSON.stringify(this.defaultSubFormTemplate));
        } else {
          this.formResponse = JSON.parse(JSON.stringify(this.initialData.subFormData));
        }
      }
    },
  },

  computed: {
    relationNameError() {
      if (this.v$.formResponse.relationName.$error && this.v$.formResponse.relationName.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
      }

      if (this.v$.formResponse.relationName.$error && this.v$.formResponse.relationName.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }

      return 'Please include a response for name of connected individual.';
    },

    workPlaceError() {
      if (this.v$.formResponse.connectionName.$error && this.v$.formResponse.connectionName.JOIN_NOW_NAME_VALIDATOR.$invalid) {
        return "We can't accept that input - please remove numbers, symbols and/or consecutive spaces.";
      }

      if (this.v$.formResponse.connectionName.$error && this.v$.formResponse.connectionName.maxLength.$invalid) {
        return 'You have exceeded the maximum amount of characters.';
      }

      return `Please include a response for ${this.formLabels.connectionInfo.connectionNameLabel}.`;
    },
  },

  mounted() {
    this.formResponse = { ...this.subFormData };
    this.updateFormFields();
  },

  validations: {
    formResponse: {
      relationship: {
        required,
      },
      relationName: {
        required,
        JOIN_NOW_NAME_VALIDATOR,
        maxLength: maxLength(480),
      },
      connectionType: {
        required,
      },
      connectionName: {
        required: requiredIf(function () {
          return this.formResponse.connectionType === Eligibility.DPS || this.formResponse.connectionType === Eligibility.CONTRACTOR;
        }),
        JOIN_NOW_NAME_VALIDATOR,
        maxLength: maxLength(480),
      },
    },
    veteranInfoIsInvalid: {
      isVeteranValid(value) {
        if (this.formResponse.connectionType === Eligibility.SERVING || this.formResponse.connectionType === Eligibility.EX_SERVING) {
          return isInvalid(value);
        }
        return true;
      },
    },
  },

  methods: {
    ...mapMutations(['updateEligibilityFormData']),

    validationStatus(validation) {
      // TODO: find out how to modularise this instead of using it multiple times across child components
      return {
        error: validation.$error,
        dirty: validation.$dirty,
      };
    },
    showDeclaration() {
      this.$emit('showDeclaration', this.formResponse.connectionType);
    },

    updateFormFields() {
      if (this.formResponse.connectionType !== '') {
        this.showDeclaration();
      }
      this.formLabels.veteranInfo.serviceAgencyLabel = getServiceAgencyLabel(this.formResponse.connectionType, true);
      this.formLabels.veteranInfo.serviceTypeLabel = getServicePeriodLabel(this.formResponse.connectionType, true);
      this.formLabels.connectionInfo.connectionNameLabel = getEligibilityWorkLabel(this.formResponse.connectionType);
      this.formLabels.connectionInfo.connectionNamePlaceholder = getEligibilityWorkPlaceHolder(this.formResponse.connectionType);

      // Contractor and public service connections will need veteran information removed
      const connectionType = this.formResponse.connectionType;
      if (connectionType === Eligibility.DPS || connectionType === Eligibility.CONTRACTOR) {
        this.formResponse.veteranInfo = { ...DEFAULT_VETERAN_INFO };
      } else if (connectionType === Eligibility.SERVING || connectionType === Eligibility.EX_SERVING) {
        this.formResponse.connectionName = '';
      }
    },

    updateVeteranInfo(data) {
      this.formResponse.veteranInfo = JSON.parse(JSON.stringify(data.veteranInfo));
      this.veteranInfoIsInvalid = data.isInvalid;
    },

    returnWhiteSpaceIfEmptyStr,
  },
};
</script>

<style lang="scss" scoped>
#supporting {
  .form-section {
    margin-bottom: rem(25);
  }
}
</style>
